import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useTranslation} from 'react-i18next';

import {KYC_SUBMITTED_EVENTS} from '@/app/auth/events/KycSubmittedEvents';

import {KYC_SUBMITTED_VIEW_MODEL} from './constants';
import {useKycSubmittedTrackers} from './trackers';

//#region [---- [TYPES] ----]
export const KYC_SUBMITTED_PHASE = {
  LOADING: 'loading',
  SURVEY: 'survey',
  WAVING: 'waving',
  LONG_WAIT: 'long_wait',
} as const;

export type TKycSubmittedPhase =
  (typeof KYC_SUBMITTED_PHASE)[keyof typeof KYC_SUBMITTED_PHASE];

type ViewModelContextType = {
  phase: TKycSubmittedPhase;
  selectedSurveyOption: string | null;
  surveyOptionIds: string[];
  onSelectSurveyOption: (optionId: string) => void;
};
//#endregion

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

export function KycSubmittedViewModelProvider({
  children,
}: {
  children: ReactNode;
}) {
  //#region [---- [TRANSLATION] ----]
  const {t} = useTranslation();
  //#endregion

  //#region [---- [HOOKS] ----]
  const {trackKycSubmitted, trackSelectKycSurvey} = useKycSubmittedTrackers();
  //#endregion

  //#region [---- [STATE] ----]
  const [phase, setPhase] = useState<TKycSubmittedPhase>(
    KYC_SUBMITTED_PHASE.LOADING,
  );
  const [selectedSurveyOption, setSelectedSurveyOption] = useState<
    string | null
  >(null);
  //#endregion

  //#region [---- [EFFECTS] ----]
  useEffect(() => {
    trackKycSubmitted(KYC_SUBMITTED_EVENTS.VIEW_KYC_SUBMITTED_PAGE);
    const surveyTimeoutId = setTimeout(() => {
      setPhase(currentPhase =>
        currentPhase === KYC_SUBMITTED_PHASE.LOADING
          ? KYC_SUBMITTED_PHASE.SURVEY
          : currentPhase,
      );
    }, KYC_SUBMITTED_VIEW_MODEL.SURVEY_DISPLAY_DELAY_MS);
    const longWaitTimeoutId = setTimeout(() => {
      setPhase(KYC_SUBMITTED_PHASE.LONG_WAIT);
    }, KYC_SUBMITTED_VIEW_MODEL.LONG_WAIT_PHASE_DELAY_MS);

    return () => {
      clearTimeout(surveyTimeoutId);
      clearTimeout(longWaitTimeoutId);
    };
  }, [trackKycSubmitted]);
  //#endregion

  //#region [---- [ACTIONS] ----]
  const onSelectSurveyOption = useCallback(
    (optionId: string) => {
      trackSelectKycSurvey(
        KYC_SUBMITTED_EVENTS.SELECT_KYC_SURVEY,
        t('kyc.kyc_submitted.survey.question'),
        t(`kyc.kyc_submitted.survey.options.${optionId}`),
      );
      setSelectedSurveyOption(optionId);
      setPhase(KYC_SUBMITTED_PHASE.WAVING);
    },
    [t, trackSelectKycSurvey],
  );
  //#endregion

  //#region [---- [CONTEXT VALUE] ----]
  const value = useMemo(
    () => ({
      phase,
      selectedSurveyOption,
      surveyOptionIds: [...KYC_SUBMITTED_VIEW_MODEL.SURVEY_OPTION_IDS],
      onSelectSurveyOption,
    }),
    [onSelectSurveyOption, phase, selectedSurveyOption],
  );
  //#endregion

  return (
    <ViewModelContext.Provider value={value}>
      {children}
    </ViewModelContext.Provider>
  );
}

export function useKycSubmittedViewModelProvider() {
  const context = useContext(ViewModelContext);
  if (context === undefined) {
    throw new Error('KycSubmitted ViewModelContext is undefined');
  }
  return context;
}
